import { defineMessages } from "../define";

/**
 * Monitoring board: the Cameras tab (monitoring.cameras.*) and the Pizza flow tab (monitoring.pipeline.*).
 * Shared words (verdicts, series, columns) live in monitoring.ts.
 */
export default defineMessages({
  en: {
    // --- cameras: summary -----------------------------------------------------------------------------
    "monitoring.cameras.summary.watched": "Watched cameras",
    "monitoring.cameras.summary.watchedHint":
      "Cameras assigned to a branch that captures photos (enable_acquisition)",
    "monitoring.cameras.summary.watchedDetail": "{offline} offline · {unknown} unknown",
    "monitoring.cameras.summary.thingsboard": "In ThingsBoard",
    "monitoring.cameras.summary.thingsboardDetail": "{assigned} assigned · {unassigned} unassigned",
    "monitoring.cameras.summary.problems": "Cameras with a problem",
    "monitoring.cameras.summary.problemsDetail": "{critical} critical · {warning} warnings",
    "monitoring.cameras.summary.captured": "Captured (24 h)",
    "monitoring.cameras.summary.capturedDetail": "branches with a camera: {branches}",
    "monitoring.cameras.summary.stuck": "Stuck pizzas (24 h)",
    "monitoring.cameras.summary.stuckHint":
      "Photographed at a branch with a camera more than 3 min ago and still without a verdict",
    "monitoring.cameras.summary.stuckDetail": "{noPhoto} without a photo · {uncaptured} packed, never photographed",
    "monitoring.cameras.summary.api": "ThingsBoard API",
    "monitoring.cameras.summary.apiHint":
      "How long the device list took from ThingsBoard (the board caches it for a short while)",

    // --- cameras: list -------------------------------------------------------------------------------
    "monitoring.cameras.list.title": "Cameras",
    "monitoring.cameras.list.description":
      "Problem cameras first. Pizza numbers are of the camera's branch over the last 24 h; trigger, upload and heartbeat come from this backend instance since its start.",
    "monitoring.cameras.list.empty": "No camera matches the filter.",
    "monitoring.cameras.list.none": "ThingsBoard returned no camera.",
    "monitoring.cameras.filter.label": "Show cameras",
    "monitoring.cameras.filter.all": "All",
    "monitoring.cameras.filter.problems": "Problems",
    "monitoring.cameras.filter.assigned": "Assigned",
    "monitoring.cameras.filter.unassigned": "Unassigned",
    "monitoring.cameras.search": "Search camera or branch",
    "monitoring.cameras.col.camera": "Camera",
    "monitoring.cameras.col.seen": "Last seen",
    "monitoring.cameras.col.captures": "Captures 24 h",
    "monitoring.cameras.col.invalid": "Invalid photos 24 h",
    "monitoring.cameras.col.instance": "This instance",
    "monitoring.cameras.details": "Show details",
    "monitoring.cameras.hideDetails": "Hide details",
    "monitoring.cameras.unassigned": "Not assigned to a branch",
    "monitoring.cameras.ofOrders.one": "· {count} order",
    "monitoring.cameras.ofOrders.few": "· {count} orders",
    "monitoring.cameras.ofOrders.other": "· {count} orders",
    "monitoring.cameras.stuckShort": "stuck",
    "monitoring.cameras.uncapturedShort": "not photographed",

    // branch flags
    "monitoring.cameras.flag.captureOn": "Capture on",
    "monitoring.cameras.flag.captureOnHint": "enable_acquisition: the pizzas of this branch are photographed",
    "monitoring.cameras.flag.captureOff": "Capture off",
    "monitoring.cameras.flag.captureOffHint": "enable_acquisition is off: this branch is not photographed",
    "monitoring.cameras.flag.bbTrigger": "Packing trigger",
    "monitoring.cameras.flag.bbTriggerHint": "The camera fires when the Balení (packing) step finishes in BiteBerry",
    "monitoring.cameras.flag.manualTrigger": "App trigger",
    "monitoring.cameras.flag.manualTriggerHint":
      "enable_bb_trigger is off: the staff start the capture from the mobile app",

    // last seen / activity
    "monitoring.cameras.seen.thingsboard": "ThingsBoard",
    "monitoring.cameras.seen.heartbeat": "Heartbeat",
    "monitoring.cameras.seen.heartbeatHint":
      "Last time the camera polled its restart flags (GET /api/camera/flags) on this instance",
    "monitoring.cameras.seen.lastCapture": "Last capture",
    "monitoring.cameras.latency.upload": "upload p50/p95",
    "monitoring.cameras.latency.verdict": "→ verdict p95",
    "monitoring.cameras.instance.trigger": "Trigger",
    "monitoring.cameras.instance.upload": "Upload",
    "monitoring.cameras.instance.failed": "failed",
    "monitoring.cameras.instance.counts": "{triggers} triggers · {uploads} uploads",
    "monitoring.cameras.instance.failedCount": "{count} failed",
    "monitoring.cameras.instance.refusedCount": "{count} refused",
    "monitoring.cameras.instanceHint":
      "Trigger, upload and heartbeat numbers are kept in memory by this backend instance since it started (a restart clears them; other instances are not included).",

    // problems
    "monitoring.cameras.problem.stuck.one": "{count} stuck pizza",
    "monitoring.cameras.problem.stuck.few": "{count} stuck pizzas",
    "monitoring.cameras.problem.stuck.other": "{count} stuck pizzas",
    "monitoring.cameras.problem.stuckHint":
      "Pizzas of this branch photographed in the last 24 h that have no verdict after 3 min. A photo that never arrived is the camera's problem; one that arrived failed in the evaluation (backend / GPU pod).",
    "monitoring.cameras.problem.stuckNoPhoto": "the photo never arrived",
    "monitoring.cameras.problem.stuckEvaluation": "photo arrived, the evaluation failed",
    "monitoring.cameras.problem.stuckMixed.one": "{count} without a photo",
    "monitoring.cameras.problem.stuckMixed.few": "{count} without a photo",
    "monitoring.cameras.problem.stuckMixed.other": "{count} without a photo",
    "monitoring.cameras.problem.uncaptured.one": "{count} packed, not photographed",
    "monitoring.cameras.problem.uncaptured.few": "{count} packed, not photographed",
    "monitoring.cameras.problem.uncaptured.other": "{count} packed, not photographed",
    "monitoring.cameras.problem.uncapturedHint":
      "Packed more than 5 min ago but never photographed: the camera was not triggered",
    "monitoring.cameras.problem.triggerFailures.one": "{count} failed trigger",
    "monitoring.cameras.problem.triggerFailures.few": "{count} failed triggers",
    "monitoring.cameras.problem.triggerFailures.other": "{count} failed triggers",
    "monitoring.cameras.problem.uploadRejections.one": "{count} refused upload",
    "monitoring.cameras.problem.uploadRejections.few": "{count} refused uploads",
    "monitoring.cameras.problem.uploadRejections.other": "{count} refused uploads",
    "monitoring.cameras.problem.lastFailed": "the last one too",
    "monitoring.cameras.problem.restartPending": "Restart requested, not picked up",
    "monitoring.cameras.problem.invalidShare": "{share} invalid photos",

    // details
    "monitoring.cameras.detail.thingsboard": "ThingsBoard",
    "monitoring.cameras.detail.branch24h": "Branch, last 24 h",
    "monitoring.cameras.detail.device": "Device",
    "monitoring.cameras.detail.status": "Status",
    "monitoring.cameras.detail.inactivityAlarm": "Inactivity alarm",
    "monitoring.cameras.detail.uncaptured": "Packed, never photographed",
    "monitoring.cameras.detail.invalidShare": "Invalid photo or no pizza",
    "monitoring.cameras.detail.lastOrder": "Last order",
    "monitoring.cameras.detail.uploadDelay": "Upload delay p50 / p95 / max",
    "monitoring.cameras.detail.captureToVerdict": "Capture → verdict p50 / p95 / max",
    "monitoring.cameras.detail.triggers": "Triggers (failed)",
    "monitoring.cameras.detail.lastTrigger": "Last trigger",
    "monitoring.cameras.detail.lastTriggerError": "Last trigger error",
    "monitoring.cameras.detail.uploads": "Uploads (refused)",
    "monitoring.cameras.detail.lastUpload": "Last accepted upload",
    "monitoring.cameras.detail.lastUploadAttempt": "Last upload attempt",
    "monitoring.cameras.detail.lastUploadDelay": "Last upload delay",
    "monitoring.cameras.detail.lastUploadError": "Last upload error",
    "monitoring.cameras.detail.flagPolls": "Heartbeats",
    "monitoring.cameras.detail.deviceId": "Device id",
    "monitoring.cameras.detail.name": "Name",
    "monitoring.cameras.detail.label": "Label",
    "monitoring.cameras.detail.type": "Type",
    "monitoring.cameras.detail.created": "Created",
    "monitoring.cameras.detail.branchId": "Branch id",
    "monitoring.cameras.detail.restartFlags": "Restart flags",
    "monitoring.cameras.detail.restartNone": "none",
    "monitoring.cameras.detail.restartCamera": "camera restart",
    "monitoring.cameras.detail.restartService": "service restart",

    // charts
    "monitoring.cameras.chart.triggers": "Capture triggers",
    "monitoring.cameras.chart.triggersDescription": "Every capture the backend asked a camera for, by outcome ({window})",
    "monitoring.cameras.chart.uploads": "Photo uploads",
    "monitoring.cameras.chart.uploadsDescription": "Photos sent by the cameras, by result ({window})",
    "monitoring.cameras.chart.uploadDelay": "Upload delay",
    "monitoring.cameras.chart.uploadDelayDescription": "From the trigger to the photo arriving at the backend ({window})",
    "monitoring.cameras.chart.backendSide": "Trigger RPC and S3 put",
    "monitoring.cameras.chart.backendSideDescription":
      "p95 of the ThingsBoard RPC that fires the camera and of storing the photo in S3 ({window})",
    "monitoring.cameras.series.triggered": "Triggered",
    "monitoring.cameras.series.deviceError": "Camera reported an error",
    "monitoring.cameras.series.noCamera": "No camera at branch",
    "monitoring.cameras.series.triggerFailed": "Trigger failed",
    "monitoring.cameras.series.mock": "Mock capture",
    "monitoring.cameras.series.uploadOk": "Accepted",
    "monitoring.cameras.series.uploadRejected": "Refused (4xx)",
    "monitoring.cameras.series.uploadFailed": "Failed (5xx)",
    "monitoring.cameras.series.servicePhoto": "Service photo",
    "monitoring.cameras.series.triggerRpc": "Trigger RPC p95",
    "monitoring.cameras.series.triggerRpcShort": "Trigger RPC",
    "monitoring.cameras.series.s3Put": "S3 put p95",
    "monitoring.cameras.series.s3PutShort": "S3 put",

    // --- pizza flow: shared -------------------------------------------------------------------------
    "monitoring.pipeline.unnamedPizza": "Unnamed pizza",
    "monitoring.pipeline.order": "Order",
    "monitoring.pipeline.more": "Showing {shown} of {total}",
    "monitoring.pipeline.ordersCount.one": "{count} order",
    "monitoring.pipeline.ordersCount.few": "{count} orders",
    "monitoring.pipeline.ordersCount.other": "{count} orders",
    "monitoring.pipeline.captureCount.one": "captured {count}×",
    "monitoring.pipeline.captureCount.few": "captured {count}×",
    "monitoring.pipeline.captureCount.other": "captured {count}×",
    "monitoring.pipeline.col.n": "Count",
    "monitoring.pipeline.col.avg": "Avg",
    "monitoring.pipeline.col.max": "Max",
    "monitoring.pipeline.col.step": "Step",

    // totals
    "monitoring.pipeline.totals.usual": "usually {value}",
    "monitoring.pipeline.totals.evaluated": "Evaluated",
    "monitoring.pipeline.totals.evaluatedDetail": "{share} of captured",
    "monitoring.pipeline.totals.share": "{share} of evaluated",
    "monitoring.pipeline.totals.photoDetail": "{invalid} invalid · {noPizza} no pizza · {share}",
    "monitoring.pipeline.totals.stuckDetail": "{withPhoto} with photo · {withoutPhoto} without",
    "monitoring.pipeline.totals.stuckNone": "nothing stuck",
    "monitoring.pipeline.totals.pendingDetail": "captured less than 3 min ago",
    "monitoring.pipeline.totals.recaptures": "Recaptures",
    "monitoring.pipeline.totals.recapturesDetail": "pizzas photographed more than once",
    "monitoring.pipeline.totals.query": "Query {ms} · {ago} · from the pizza and order data",

    // flow + latency
    "monitoring.pipeline.flow.baseline": "Dashed: average of the same time of day over the previous {days} days",
    "monitoring.pipeline.flow.noBaseline": "The usual volume is shown for windows up to 24 h.",
    "monitoring.pipeline.latency.title": "Latency of the pizza flow",
    "monitoring.pipeline.latency.description": "p95 per interval ({window}); the p50 lines are dashed, switch them on in the legend",
    "monitoring.pipeline.latency.stage": "Stage",
    "monitoring.pipeline.latency.trigger_rpc_ms": "Trigger RPC",
    "monitoring.pipeline.latency.trigger_rpc_msHint": "The ThingsBoard RPC that fires the camera (capture_duration)",
    "monitoring.pipeline.latency.upload_delay_ms": "Upload delay",
    "monitoring.pipeline.latency.upload_delay_msHint": "From the trigger to the photo arriving (image_download_duration)",
    "monitoring.pipeline.latency.pipeline_ms": "Evaluation pipeline",
    "monitoring.pipeline.latency.pipeline_msHint": "Evaluation of the photo (processing_times.total)",
    "monitoring.pipeline.latency.capture_to_verdict_ms": "Capture → verdict",
    "monitoring.pipeline.latency.capture_to_verdict_msHint":
      "From the capture to the stored verdict (capture_at → evaluated_at)",

    // steps + evaluations
    "monitoring.pipeline.steps.title": "Pipeline steps",
    "monitoring.pipeline.steps.description":
      "Average time of the evaluation steps for pizzas captured in the {window}, in the order they run",
    "monitoring.pipeline.steps.share": "Share of the total",
    "monitoring.pipeline.steps.shareHint": "Average of the step divided by the average of the whole pipeline",
    "monitoring.pipeline.steps.empty": "No evaluation with step times in this window.",
    "monitoring.pipeline.steps.totalAvg": "Whole pipeline, average",
    "monitoring.pipeline.steps.total": "Total",
    "monitoring.pipeline.steps.totalHint":
      "processing_times.total: from loading the photo to the verdict, without storing the result",
    "monitoring.pipeline.steps.parallel": "Side by side: the longest branch counts",
    "monitoring.pipeline.steps.parallelHint":
      "These steps run at the same time (phase 2 on the GPU pod, the burnt and blur CNNs on the backend); the pipeline waits for the longest branch, which is what the share and the total count",
    "monitoring.pipeline.steps.branches.one": "{count} branch",
    "monitoring.pipeline.steps.branches.few": "{count} branches",
    "monitoring.pipeline.steps.branches.other": "{count} branches",
    "monitoring.pipeline.steps.detailHint": "A finer timing of the step above; not added to the total",
    "monitoring.pipeline.steps.rest": "Between the steps (not measured)",
    "monitoring.pipeline.steps.restHint":
      "Average of the whole minus the measured steps (the side-by-side stage by its longest branch)",
    "monitoring.pipeline.steps.population":
      "Fully evaluated pizzas only: \"Invalid photo\" and \"No pizza\" verdicts store no step times. Step averages are over the pizzas where the step ran.",
    "monitoring.pipeline.step.load_pizza_image": "Load the photo",
    "monitoring.pipeline.step.calculate_crust": "Crust (GPU pod)",
    "monitoring.pipeline.step.pizza_presence": "Is there a pizza?",
    "monitoring.pipeline.step.calculate_pizza_properties": "Pizza geometry",
    "monitoring.pipeline.step.analyze_pizza_geometry": "Geometry analysis",
    "monitoring.pipeline.step.gpu_phase2": "GPU phase 2 (components, obstacles, slicing)",
    "monitoring.pipeline.step.gpu_phase2_crop": "Pizza crop for the pod",
    "monitoring.pipeline.step.burnt_cnn": "Doneness (burnt CNN)",
    "monitoring.pipeline.step.burnt_cnn_unwrap": "Crust strip unwrap",
    "monitoring.pipeline.step.burnt_cnn_inference": "Inference",
    "monitoring.pipeline.step.burnt_cnn_heatmap": "Doneness heatmap",
    "monitoring.pipeline.step.blur_cnn_crop": "Blur: crop",
    "monitoring.pipeline.step.blur_cnn_inference": "Blur: inference",
    "monitoring.pipeline.step.evaluate_photo_quality": "Photo quality",
    "monitoring.pipeline.step.obstacle_seg_inference": "Obstacles: inference",
    "monitoring.pipeline.step.obstacle_seg_postprocess": "Obstacles: mask post-processing",
    "monitoring.pipeline.step.slicing_cnn": "Slicing",
    "monitoring.pipeline.step.component_detection": "Component detection",
    "monitoring.pipeline.step.component_evaluation": "Component evaluation",
    "monitoring.pipeline.step.overall_evaluation": "Overall verdict",
    "monitoring.pipeline.evaluations.title": "Evaluations",
    "monitoring.pipeline.evaluations.description": "Finished and failed evaluations counted by the backend ({window})",
    "monitoring.pipeline.evaluations.failedShare": "Failure rate",
    "monitoring.pipeline.evaluations.classes": "By classification",
    "monitoring.pipeline.series.evaluationOk": "Evaluated",
    "monitoring.pipeline.series.evaluationFailed": "Failed",
    "monitoring.pipeline.pipelineTime.title": "Evaluation pipeline time",
    "monitoring.pipeline.pipelineTime.description": "evaluation.pipeline_ms per interval, p50 in the legend ({window})",

    // funnel
    "monitoring.pipeline.funnel.title": "Order funnel",
    "monitoring.pipeline.funnel.description":
      "Pizzas of the orders since {time} (the last 6 h at most), followed step by step",
    "monitoring.pipeline.funnel.ordered": "Ordered",
    "monitoring.pipeline.funnel.packed": "Packed",
    "monitoring.pipeline.funnel.captured": "Photographed",
    "monitoring.pipeline.funnel.evaluated": "Evaluated",
    "monitoring.pipeline.funnel.drop.packed": "{count} not packed yet",
    "monitoring.pipeline.funnel.drop.captured": "{count} without a photo",
    "monitoring.pipeline.funnel.drop.evaluated": "{count} without a verdict",
    "monitoring.pipeline.funnel.noDrop": "none lost",
    "monitoring.pipeline.funnel.uncaptured.one": "{count} packed over 5 min ago, never photographed",
    "monitoring.pipeline.funnel.uncaptured.few": "{count} packed over 5 min ago, never photographed",
    "monitoring.pipeline.funnel.uncaptured.other": "{count} packed over 5 min ago, never photographed",
    "monitoring.pipeline.funnel.uncapturedCol": "Never photographed",
    "monitoring.pipeline.funnel.empty": "No order in the last hours.",

    // stuck + uncaptured
    "monitoring.pipeline.stuck.title": "Stuck pizzas",
    "monitoring.pipeline.stuck.description": "Photographed more than 3 min ago and still without a verdict",
    "monitoring.pipeline.stuck.empty": "No stuck pizza in this window.",
    "monitoring.pipeline.stuck.photoYes": "Photo received",
    "monitoring.pipeline.stuck.photoYesHint": "the evaluation failed or hangs",
    "monitoring.pipeline.stuck.photoNo": "No photo",
    "monitoring.pipeline.stuck.photoNoHint": "the camera never delivered the photo",
    "monitoring.pipeline.uncaptured.title": "Packed, never photographed",
    "monitoring.pipeline.uncaptured.description":
      "Packed more than 5 min ago at a branch that captures photos, without any capture (the last 6 h at most)",
    "monitoring.pipeline.uncaptured.empty": "Every packed pizza got its photo.",
    "monitoring.pipeline.uncaptured.manual": "App trigger branch",
    "monitoring.pipeline.uncaptured.packed": "packed",

    // branches
    "monitoring.pipeline.branches.title": "Branches",
    "monitoring.pipeline.branches.description": "Pizza flow per branch ({window})",
    "monitoring.pipeline.branches.sort": "Sort branches by",
    "monitoring.pipeline.branches.sort.captured": "Captured",
    "monitoring.pipeline.branches.sort.orders": "Orders",
    "monitoring.pipeline.branches.sort.stuck": "Stuck",
    "monitoring.pipeline.branches.sort.invalid": "Invalid photo share",
    "monitoring.pipeline.branches.sort.nok": "NOK share",
    "monitoring.pipeline.branches.sort.latency": "Capture → verdict p95",
    "monitoring.pipeline.branches.sort.lastCapture": "Last capture",
    "monitoring.pipeline.branches.sort.name": "Name",
    "monitoring.pipeline.branches.asc": "Ascending",
    "monitoring.pipeline.branches.desc": "Descending",
    "monitoring.pipeline.branches.verdicts": "Verdicts",
    "monitoring.pipeline.branches.invalidShare": "Invalid photos",
    "monitoring.pipeline.branches.nokShare": "NOK share",
    "monitoring.pipeline.branches.toVerdict": "→ verdict p95",
    "monitoring.pipeline.branches.uploadDelay": "Upload delay p95",
    "monitoring.pipeline.branches.trend": "Captures",
    "monitoring.pipeline.branches.evaluated": "{count} evaluated",
    "monitoring.pipeline.branches.recaptures": "{count} recaptured",
    "monitoring.pipeline.branches.photo": "Photo",
    "monitoring.pipeline.branches.quiet": "Capture on, but nothing in this window: {names}",

    // recent
    "monitoring.pipeline.recent.showLess": "Show fewer",
    "monitoring.pipeline.recent.noPhoto": "no photo",
    "monitoring.pipeline.recent.uploadDelay": "Upload delay",
  },
  cs: {
    "monitoring.cameras.summary.watched": "Sledované kamery",
    "monitoring.cameras.summary.watchedHint": "Kamery přiřazené k pobočce, která fotí pizzy (enable_acquisition)",
    "monitoring.cameras.summary.watchedDetail": "{offline} offline · {unknown} neznámý stav",
    "monitoring.cameras.summary.thingsboard": "V ThingsBoardu",
    "monitoring.cameras.summary.thingsboardDetail": "{assigned} přiřazených · {unassigned} nepřiřazených",
    "monitoring.cameras.summary.problems": "Kamery s problémem",
    "monitoring.cameras.summary.problemsDetail": "{critical} kritických · {warning} varování",
    "monitoring.cameras.summary.captured": "Vyfoceno (24 h)",
    "monitoring.cameras.summary.capturedDetail": "pobočky s kamerou: {branches}",
    "monitoring.cameras.summary.stuck": "Zaseklé pizzy (24 h)",
    "monitoring.cameras.summary.stuckHint":
      "Vyfocené na pobočce s kamerou před víc než 3 min a stále bez verdiktu",
    "monitoring.cameras.summary.stuckDetail": "{noPhoto} bez fotky · {uncaptured} zabaleno, nikdy nevyfoceno",
    "monitoring.cameras.summary.api": "ThingsBoard API",
    "monitoring.cameras.summary.apiHint":
      "Jak dlouho trvalo načíst seznam zařízení z ThingsBoardu (board ho krátce drží v cache)",

    "monitoring.cameras.list.title": "Kamery",
    "monitoring.cameras.list.description":
      "Kamery s problémem jsou nahoře. Počty pizz jsou za pobočku kamery za posledních 24 h; spuštění, nahrání a heartbeat vidí tato instance backendu od svého startu.",
    "monitoring.cameras.list.empty": "Filtru neodpovídá žádná kamera.",
    "monitoring.cameras.list.none": "ThingsBoard nevrátil žádnou kameru.",
    "monitoring.cameras.filter.label": "Zobrazit kamery",
    "monitoring.cameras.filter.all": "Všechny",
    "monitoring.cameras.filter.problems": "Problémy",
    "monitoring.cameras.filter.assigned": "Přiřazené",
    "monitoring.cameras.filter.unassigned": "Nepřiřazené",
    "monitoring.cameras.search": "Hledat kameru nebo pobočku",
    "monitoring.cameras.col.camera": "Kamera",
    "monitoring.cameras.col.seen": "Naposledy viděna",
    "monitoring.cameras.col.captures": "Snímky 24 h",
    "monitoring.cameras.col.invalid": "Neplatné fotky 24 h",
    "monitoring.cameras.col.instance": "Tato instance",
    "monitoring.cameras.details": "Zobrazit detail",
    "monitoring.cameras.hideDetails": "Skrýt detail",
    "monitoring.cameras.unassigned": "Nepřiřazená k pobočce",
    "monitoring.cameras.ofOrders.one": "· {count} objednávka",
    "monitoring.cameras.ofOrders.few": "· {count} objednávky",
    "monitoring.cameras.ofOrders.other": "· {count} objednávek",
    "monitoring.cameras.stuckShort": "zaseklé",
    "monitoring.cameras.uncapturedShort": "nevyfocené",

    "monitoring.cameras.flag.captureOn": "Focení zapnuto",
    "monitoring.cameras.flag.captureOnHint": "enable_acquisition: pizzy této pobočky se fotí",
    "monitoring.cameras.flag.captureOff": "Focení vypnuto",
    "monitoring.cameras.flag.captureOffHint": "enable_acquisition je vypnuté: pobočka se nefotí",
    "monitoring.cameras.flag.bbTrigger": "Spouští balení",
    "monitoring.cameras.flag.bbTriggerHint": "Kamera se spustí, když v BiteBerry skončí krok Balení",
    "monitoring.cameras.flag.manualTrigger": "Spouští aplikace",
    "monitoring.cameras.flag.manualTriggerHint":
      "enable_bb_trigger je vypnuté: focení spouští obsluha z mobilní aplikace",

    "monitoring.cameras.seen.thingsboard": "ThingsBoard",
    "monitoring.cameras.seen.heartbeat": "Heartbeat",
    "monitoring.cameras.seen.heartbeatHint":
      "Kdy si kamera naposledy na této instanci stáhla příznaky restartu (GET /api/camera/flags)",
    "monitoring.cameras.seen.lastCapture": "Poslední snímek",
    "monitoring.cameras.latency.upload": "nahrání p50/p95",
    "monitoring.cameras.latency.verdict": "→ verdikt p95",
    "monitoring.cameras.instance.trigger": "Spuštění",
    "monitoring.cameras.instance.upload": "Nahrání",
    "monitoring.cameras.instance.failed": "selhalo",
    "monitoring.cameras.instance.counts": "{triggers} spuštění · {uploads} nahrání",
    "monitoring.cameras.instance.failedCount": "{count} selhalo",
    "monitoring.cameras.instance.refusedCount": "{count} odmítnuto",
    "monitoring.cameras.instanceHint":
      "Počty spuštění, nahrání a heartbeatů drží tato instance backendu v paměti od svého startu (restart je smaže, jiné instance nejsou zahrnuté).",

    "monitoring.cameras.problem.stuck.one": "{count} zaseklá pizza",
    "monitoring.cameras.problem.stuck.few": "{count} zaseklé pizzy",
    "monitoring.cameras.problem.stuck.other": "{count} zaseklých pizz",
    "monitoring.cameras.problem.stuckHint":
      "Pizzy této pobočky vyfocené za posledních 24 h, které ani po 3 min nemají verdikt. Když fotka nedorazila, jde o kameru; když dorazila, selhalo vyhodnocení (backend / GPU pod).",
    "monitoring.cameras.problem.stuckNoPhoto": "fotka nedorazila",
    "monitoring.cameras.problem.stuckEvaluation": "fotka dorazila, selhalo vyhodnocení",
    "monitoring.cameras.problem.stuckMixed.one": "{count} bez fotky",
    "monitoring.cameras.problem.stuckMixed.few": "{count} bez fotky",
    "monitoring.cameras.problem.stuckMixed.other": "{count} bez fotky",
    "monitoring.cameras.problem.uncaptured.one": "{count} zabalená bez fotky",
    "monitoring.cameras.problem.uncaptured.few": "{count} zabalené bez fotky",
    "monitoring.cameras.problem.uncaptured.other": "{count} zabalených bez fotky",
    "monitoring.cameras.problem.uncapturedHint":
      "Zabalené před víc než 5 min, ale nikdy nevyfocené: kamera nebyla spuštěna",
    "monitoring.cameras.problem.triggerFailures.one": "{count} neúspěšné spuštění",
    "monitoring.cameras.problem.triggerFailures.few": "{count} neúspěšná spuštění",
    "monitoring.cameras.problem.triggerFailures.other": "{count} neúspěšných spuštění",
    "monitoring.cameras.problem.uploadRejections.one": "{count} odmítnuté nahrání",
    "monitoring.cameras.problem.uploadRejections.few": "{count} odmítnutá nahrání",
    "monitoring.cameras.problem.uploadRejections.other": "{count} odmítnutých nahrání",
    "monitoring.cameras.problem.lastFailed": "i to poslední",
    "monitoring.cameras.problem.restartPending": "Restart vyžádán, kamera si ho nepřevzala",
    "monitoring.cameras.problem.invalidShare": "{share} neplatných fotek",

    "monitoring.cameras.detail.thingsboard": "ThingsBoard",
    "monitoring.cameras.detail.branch24h": "Pobočka, posledních 24 h",
    "monitoring.cameras.detail.device": "Zařízení",
    "monitoring.cameras.detail.status": "Stav",
    "monitoring.cameras.detail.inactivityAlarm": "Alarm nečinnosti",
    "monitoring.cameras.detail.uncaptured": "Zabaleno, nikdy nevyfoceno",
    "monitoring.cameras.detail.invalidShare": "Neplatná fotka nebo bez pizzy",
    "monitoring.cameras.detail.lastOrder": "Poslední objednávka",
    "monitoring.cameras.detail.uploadDelay": "Zpoždění nahrání p50 / p95 / max",
    "monitoring.cameras.detail.captureToVerdict": "Vyfocení → verdikt p50 / p95 / max",
    "monitoring.cameras.detail.triggers": "Spuštění (selhalo)",
    "monitoring.cameras.detail.lastTrigger": "Poslední spuštění",
    "monitoring.cameras.detail.lastTriggerError": "Poslední chyba spuštění",
    "monitoring.cameras.detail.uploads": "Nahrání (odmítnuto)",
    "monitoring.cameras.detail.lastUpload": "Poslední přijaté nahrání",
    "monitoring.cameras.detail.lastUploadAttempt": "Poslední pokus o nahrání",
    "monitoring.cameras.detail.lastUploadDelay": "Zpoždění posledního nahrání",
    "monitoring.cameras.detail.lastUploadError": "Poslední chyba nahrání",
    "monitoring.cameras.detail.flagPolls": "Heartbeaty",
    "monitoring.cameras.detail.deviceId": "ID zařízení",
    "monitoring.cameras.detail.name": "Název",
    "monitoring.cameras.detail.label": "Popisek",
    "monitoring.cameras.detail.type": "Typ",
    "monitoring.cameras.detail.created": "Vytvořeno",
    "monitoring.cameras.detail.branchId": "ID pobočky",
    "monitoring.cameras.detail.restartFlags": "Příznaky restartu",
    "monitoring.cameras.detail.restartNone": "žádné",
    "monitoring.cameras.detail.restartCamera": "restart kamery",
    "monitoring.cameras.detail.restartService": "restart služby",

    "monitoring.cameras.chart.triggers": "Spuštění kamer",
    "monitoring.cameras.chart.triggersDescription": "Každé focení, o které backend požádal kameru, podle výsledku ({window})",
    "monitoring.cameras.chart.uploads": "Nahrání fotek",
    "monitoring.cameras.chart.uploadsDescription": "Fotky poslané kamerami podle výsledku ({window})",
    "monitoring.cameras.chart.uploadDelay": "Zpoždění nahrání",
    "monitoring.cameras.chart.uploadDelayDescription": "Od spuštění kamery po doručení fotky na backend ({window})",
    "monitoring.cameras.chart.backendSide": "RPC spuštění a uložení do S3",
    "monitoring.cameras.chart.backendSideDescription":
      "p95 RPC přes ThingsBoard, které spouští kameru, a uložení fotky do S3 ({window})",
    "monitoring.cameras.series.triggered": "Spuštěno",
    "monitoring.cameras.series.deviceError": "Kamera hlásí chybu",
    "monitoring.cameras.series.noCamera": "Pobočka bez kamery",
    "monitoring.cameras.series.triggerFailed": "Spuštění selhalo",
    "monitoring.cameras.series.mock": "Simulované focení",
    "monitoring.cameras.series.uploadOk": "Přijato",
    "monitoring.cameras.series.uploadRejected": "Odmítnuto (4xx)",
    "monitoring.cameras.series.uploadFailed": "Selhalo (5xx)",
    "monitoring.cameras.series.servicePhoto": "Servisní fotka",
    "monitoring.cameras.series.triggerRpc": "RPC spuštění p95",
    "monitoring.cameras.series.triggerRpcShort": "RPC spuštění",
    "monitoring.cameras.series.s3Put": "Uložení do S3 p95",
    "monitoring.cameras.series.s3PutShort": "Uložení do S3",

    "monitoring.pipeline.unnamedPizza": "Pizza bez názvu",
    "monitoring.pipeline.order": "Objednávka",
    "monitoring.pipeline.more": "Zobrazeno {shown} z {total}",
    "monitoring.pipeline.ordersCount.one": "{count} objednávka",
    "monitoring.pipeline.ordersCount.few": "{count} objednávky",
    "monitoring.pipeline.ordersCount.other": "{count} objednávek",
    "monitoring.pipeline.captureCount.one": "vyfoceno {count}×",
    "monitoring.pipeline.captureCount.few": "vyfoceno {count}×",
    "monitoring.pipeline.captureCount.other": "vyfoceno {count}×",
    "monitoring.pipeline.col.n": "Počet",
    "monitoring.pipeline.col.avg": "Průměr",
    "monitoring.pipeline.col.max": "Max",
    "monitoring.pipeline.col.step": "Krok",

    "monitoring.pipeline.totals.usual": "obvykle {value}",
    "monitoring.pipeline.totals.evaluated": "Vyhodnoceno",
    "monitoring.pipeline.totals.evaluatedDetail": "{share} z vyfocených",
    "monitoring.pipeline.totals.share": "{share} z vyhodnocených",
    "monitoring.pipeline.totals.photoDetail": "{invalid} neplatných · {noPizza} bez pizzy · {share}",
    "monitoring.pipeline.totals.stuckDetail": "{withPhoto} s fotkou · {withoutPhoto} bez fotky",
    "monitoring.pipeline.totals.stuckNone": "nic se nezaseklo",
    "monitoring.pipeline.totals.pendingDetail": "vyfoceno před méně než 3 min",
    "monitoring.pipeline.totals.recaptures": "Opakovaná focení",
    "monitoring.pipeline.totals.recapturesDetail": "pizzy vyfocené víckrát",
    "monitoring.pipeline.totals.query": "Dotaz {ms} · {ago} · z dat pizz a objednávek",

    "monitoring.pipeline.flow.baseline": "Čárkovaně: průměr stejné denní doby za předchozích {days} dní",
    "monitoring.pipeline.flow.noBaseline": "Obvyklý objem se ukazuje pro období do 24 h.",
    "monitoring.pipeline.latency.title": "Latence toku pizz",
    "monitoring.pipeline.latency.description": "p95 za interval ({window}); čáry p50 jsou čárkované, zapněte je v legendě",
    "monitoring.pipeline.latency.stage": "Úsek",
    "monitoring.pipeline.latency.trigger_rpc_ms": "RPC spuštění",
    "monitoring.pipeline.latency.trigger_rpc_msHint": "RPC přes ThingsBoard, které spouští kameru (capture_duration)",
    "monitoring.pipeline.latency.upload_delay_ms": "Zpoždění nahrání",
    "monitoring.pipeline.latency.upload_delay_msHint": "Od spuštění po doručení fotky (image_download_duration)",
    "monitoring.pipeline.latency.pipeline_ms": "Pipeline vyhodnocení",
    "monitoring.pipeline.latency.pipeline_msHint": "Vyhodnocení fotky (processing_times.total)",
    "monitoring.pipeline.latency.capture_to_verdict_ms": "Vyfocení → verdikt",
    "monitoring.pipeline.latency.capture_to_verdict_msHint":
      "Od vyfocení po uložený verdikt (capture_at → evaluated_at)",

    "monitoring.pipeline.steps.title": "Kroky pipeline",
    "monitoring.pipeline.steps.description":
      "Průměrná doba kroků vyhodnocení u pizz vyfocených za {window}, v pořadí, v jakém běží",
    "monitoring.pipeline.steps.share": "Podíl z celku",
    "monitoring.pipeline.steps.shareHint": "Průměr kroku vydělený průměrem celé pipeline",
    "monitoring.pipeline.steps.empty": "V tomto období není žádné vyhodnocení s časy kroků.",
    "monitoring.pipeline.steps.totalAvg": "Celá pipeline, průměr",
    "monitoring.pipeline.steps.total": "Celkem",
    "monitoring.pipeline.steps.totalHint":
      "processing_times.total: od načtení fotky po verdikt, bez uložení výsledku",
    "monitoring.pipeline.steps.parallel": "Souběžně: počítá se nejdelší větev",
    "monitoring.pipeline.steps.parallelHint":
      "Tyto kroky běží zároveň (fáze 2 na GPU podu, burnt a blur CNN na backendu); pipeline čeká na nejdelší větev a jen ta se počítá do podílu i do součtu",
    "monitoring.pipeline.steps.branches.one": "{count} větev",
    "monitoring.pipeline.steps.branches.few": "{count} větve",
    "monitoring.pipeline.steps.branches.other": "{count} větví",
    "monitoring.pipeline.steps.detailHint": "Dílčí měření kroku nad ním; do součtu se nepočítá",
    "monitoring.pipeline.steps.rest": "Mezi kroky (neměřeno)",
    "monitoring.pipeline.steps.restHint":
      "Průměr celku minus měřené kroky (souběžná skupina nejdelší větví)",
    "monitoring.pipeline.steps.population":
      "Jen plně vyhodnocené pizzy: verdikty „Neplatná fotka“ a „Bez pizzy“ časy kroků neukládají. Průměr kroku je přes pizzy, u kterých krok běžel.",
    "monitoring.pipeline.step.load_pizza_image": "Načtení fotky",
    "monitoring.pipeline.step.calculate_crust": "Okraj (GPU pod)",
    "monitoring.pipeline.step.pizza_presence": "Je na fotce pizza?",
    "monitoring.pipeline.step.calculate_pizza_properties": "Geometrie pizzy",
    "monitoring.pipeline.step.analyze_pizza_geometry": "Analýza geometrie",
    "monitoring.pipeline.step.gpu_phase2": "GPU fáze 2 (suroviny, překážky, krájení)",
    "monitoring.pipeline.step.gpu_phase2_crop": "Výřez pizzy pro pod",
    "monitoring.pipeline.step.burnt_cnn": "Propečení (burnt CNN)",
    "monitoring.pipeline.step.burnt_cnn_unwrap": "Rozvinutí okraje do pásu",
    "monitoring.pipeline.step.burnt_cnn_inference": "Inference",
    "monitoring.pipeline.step.burnt_cnn_heatmap": "Heatmapa propečení",
    "monitoring.pipeline.step.blur_cnn_crop": "Rozmazání: výřez",
    "monitoring.pipeline.step.blur_cnn_inference": "Rozmazání: inference",
    "monitoring.pipeline.step.evaluate_photo_quality": "Kvalita fotky",
    "monitoring.pipeline.step.obstacle_seg_inference": "Překážky: inference",
    "monitoring.pipeline.step.obstacle_seg_postprocess": "Překážky: zpracování masky",
    "monitoring.pipeline.step.slicing_cnn": "Krájení",
    "monitoring.pipeline.step.component_detection": "Detekce surovin",
    "monitoring.pipeline.step.component_evaluation": "Vyhodnocení surovin",
    "monitoring.pipeline.step.overall_evaluation": "Celkový verdikt",
    "monitoring.pipeline.evaluations.title": "Vyhodnocení",
    "monitoring.pipeline.evaluations.description": "Dokončená a neúspěšná vyhodnocení podle backendu ({window})",
    "monitoring.pipeline.evaluations.failedShare": "Podíl selhání",
    "monitoring.pipeline.evaluations.classes": "Podle klasifikace",
    "monitoring.pipeline.series.evaluationOk": "Vyhodnoceno",
    "monitoring.pipeline.series.evaluationFailed": "Selhalo",
    "monitoring.pipeline.pipelineTime.title": "Doba pipeline vyhodnocení",
    "monitoring.pipeline.pipelineTime.description": "evaluation.pipeline_ms za interval, p50 v legendě ({window})",

    "monitoring.pipeline.funnel.title": "Trychtýř objednávek",
    "monitoring.pipeline.funnel.description":
      "Pizzy z objednávek od {time} (nejvýš posledních 6 h), sledované krok po kroku",
    "monitoring.pipeline.funnel.ordered": "Objednáno",
    "monitoring.pipeline.funnel.packed": "Zabaleno",
    "monitoring.pipeline.funnel.captured": "Vyfoceno",
    "monitoring.pipeline.funnel.evaluated": "Vyhodnoceno",
    "monitoring.pipeline.funnel.drop.packed": "{count} ještě nezabaleno",
    "monitoring.pipeline.funnel.drop.captured": "{count} bez fotky",
    "monitoring.pipeline.funnel.drop.evaluated": "{count} bez verdiktu",
    "monitoring.pipeline.funnel.noDrop": "nic se neztratilo",
    "monitoring.pipeline.funnel.uncaptured.one": "{count} zabalená před víc než 5 min, nevyfocená",
    "monitoring.pipeline.funnel.uncaptured.few": "{count} zabalené před víc než 5 min, nevyfocené",
    "monitoring.pipeline.funnel.uncaptured.other": "{count} zabalených před víc než 5 min, nevyfocených",
    "monitoring.pipeline.funnel.uncapturedCol": "Nevyfoceno",
    "monitoring.pipeline.funnel.empty": "Za poslední hodiny nepřišla žádná objednávka.",

    "monitoring.pipeline.stuck.title": "Zaseklé pizzy",
    "monitoring.pipeline.stuck.description": "Vyfocené před víc než 3 min a stále bez verdiktu",
    "monitoring.pipeline.stuck.empty": "V tomto období se žádná pizza nezasekla.",
    "monitoring.pipeline.stuck.photoYes": "Fotka dorazila",
    "monitoring.pipeline.stuck.photoYesHint": "vyhodnocení selhalo nebo visí",
    "monitoring.pipeline.stuck.photoNo": "Bez fotky",
    "monitoring.pipeline.stuck.photoNoHint": "kamera fotku nikdy nedoručila",
    "monitoring.pipeline.uncaptured.title": "Zabalené, nikdy nevyfocené",
    "monitoring.pipeline.uncaptured.description":
      "Zabalené před víc než 5 min na pobočce, která fotí, a bez jediného snímku (nejvýš posledních 6 h)",
    "monitoring.pipeline.uncaptured.empty": "Každá zabalená pizza má fotku.",
    "monitoring.pipeline.uncaptured.manual": "Focení spouští aplikace",
    "monitoring.pipeline.uncaptured.packed": "zabaleno",

    "monitoring.pipeline.branches.title": "Pobočky",
    "monitoring.pipeline.branches.description": "Tok pizz po pobočkách ({window})",
    "monitoring.pipeline.branches.sort": "Řadit pobočky podle",
    "monitoring.pipeline.branches.sort.captured": "Vyfoceno",
    "monitoring.pipeline.branches.sort.orders": "Objednávky",
    "monitoring.pipeline.branches.sort.stuck": "Zaseklé",
    "monitoring.pipeline.branches.sort.invalid": "Podíl neplatných fotek",
    "monitoring.pipeline.branches.sort.nok": "Podíl NEOK",
    "monitoring.pipeline.branches.sort.latency": "Vyfocení → verdikt p95",
    "monitoring.pipeline.branches.sort.lastCapture": "Poslední snímek",
    "monitoring.pipeline.branches.sort.name": "Název",
    "monitoring.pipeline.branches.asc": "Vzestupně",
    "monitoring.pipeline.branches.desc": "Sestupně",
    "monitoring.pipeline.branches.verdicts": "Verdikty",
    "monitoring.pipeline.branches.invalidShare": "Neplatné fotky",
    "monitoring.pipeline.branches.nokShare": "Podíl NEOK",
    "monitoring.pipeline.branches.toVerdict": "→ verdikt p95",
    "monitoring.pipeline.branches.uploadDelay": "Zpoždění nahrání p95",
    "monitoring.pipeline.branches.trend": "Snímky",
    "monitoring.pipeline.branches.evaluated": "{count} vyhodnoceno",
    "monitoring.pipeline.branches.recaptures": "{count} znovu vyfoceno",
    "monitoring.pipeline.branches.photo": "Fotka",
    "monitoring.pipeline.branches.quiet": "Focení zapnuto, ale v tomto období nic: {names}",

    "monitoring.pipeline.recent.showLess": "Zobrazit méně",
    "monitoring.pipeline.recent.noPhoto": "bez fotky",
    "monitoring.pipeline.recent.uploadDelay": "Zpoždění nahrání",
  },
});
